// =============================================
// Batch Orchestrator Service
// v3.1: Atomic claim loop (FOR UPDATE SKIP LOCKED via RPC) + heartbeat + lock cleanup
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { generateArticle } from "@/services/contentEngine";
import { isWorkerModeEnabled } from "@/lib/workerMode";
import { enqueueGenerationJob } from "@/services/queueClient";

// =============================================
// Config
// =============================================

export interface BatchConfig {
  maxConcurrentGenerations: number;
  maxDailyGenerations: number;
  maxDailyCost: number;
  priorityMode: "demand_first" | "map_sequential" | "random";
  autoPublish: boolean;
  budgetMode: boolean;
}

const DEFAULT_CONFIG: BatchConfig = {
  maxConcurrentGenerations: 3,
  maxDailyGenerations: 50,
  maxDailyCost: 25,
  priorityMode: "demand_first",
  autoPublish: false,
  budgetMode: true,
};

// Lock lease length (minutes) — claim_generation_job RPC default; heartbeat extends it
const LOCK_LEASE_MINUTES = 10;
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 min

// =============================================
// State
// =============================================

let isProcessing = false;
let isPaused = false;

// =============================================
// Queue operations
// =============================================

export interface QueueStatus {
  queued: number;
  inProgress: number;
  completed: number;
  failed: number;
  todayCost: number;
  todayCount: number;
}

/**
 * Get current queue status for a map (or global).
 */
export async function getQueueStatus(mapId?: string): Promise<QueueStatus> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let query = (supabase as any).from("generation_queue").select("status, result", { count: "exact" });
  if (mapId) query = query.eq("map_id", mapId);

  const { data: items } = await query;
  const allItems = (items || []) as Array<{ status: string; result: any }>;

  // Today's items
  let todayQuery = (supabase as any)
    .from("generation_queue")
    .select("status, result")
    .gte("created_at", today.toISOString());
  if (mapId) todayQuery = todayQuery.eq("map_id", mapId);

  const { data: todayItems } = await todayQuery;
  const todayCompleted = (todayItems || []).filter((i: any) => i.status === "completed");

  // Sum today's costs
  let todayCost = 0;
  for (const item of todayCompleted) {
    todayCost += item.result?.cost?.total || 0;
  }

  return {
    queued: allItems.filter((i) => i.status === "queued").length,
    inProgress: allItems.filter((i) => i.status === "in_progress").length,
    completed: allItems.filter((i) => i.status === "completed").length,
    failed: allItems.filter((i) => i.status === "failed").length,
    todayCost,
    todayCount: todayCompleted.length,
  };
}

/**
 * Add all unprocessed nodes from a map to the generation queue.
 */
export async function queueMap(mapId: string): Promise<number> {
  const { data: session } = await supabase.auth.getSession();
  const userId = session?.session?.user?.id;

  // Get nodes that aren't already ready/published and not already queued
  const { data: nodes } = await (supabase as any)
    .from("thematic_nodes")
    .select("id, publish_priority")
    .eq("map_id", mapId)
    .not("node_status", "eq", "published");

  if (!nodes || nodes.length === 0) return 0;

  // Check which nodes are already in queue
  const { data: existingQueue } = await (supabase as any)
    .from("generation_queue")
    .select("node_id")
    .eq("map_id", mapId)
    .in("status", ["queued", "in_progress"]);

  const queuedNodeIds = new Set((existingQueue || []).map((q: any) => q.node_id));

  // Check which nodes already have content ready
  const { data: contentReady } = await (supabase as any)
    .from("thematic_node_content")
    .select("node_id")
    .eq("map_id", mapId)
    .in("engine_stage", ["ready", "published"]);

  const readyNodeIds = new Set((contentReady || []).map((c: any) => c.node_id));

  // Filter nodes to queue
  const toQueue = nodes.filter(
    (n: any) => !queuedNodeIds.has(n.id) && !readyNodeIds.has(n.id),
  );

  if (toQueue.length === 0) return 0;

  // Insert into queue
  const queueItems = toQueue.map((n: any) => ({
    user_id: userId,
    map_id: mapId,
    node_id: n.id,
    step: "full_article",
    status: "queued",
    priority: n.publish_priority || 50,
    config: {},
  }));

  await (supabase as any).from("generation_queue").insert(queueItems);

  return toQueue.length;
}

/**
 * Add specific nodes to the generation queue.
 */
export async function queueNodes(
  nodeIds: string[],
  mapId: string,
): Promise<number> {
  const { data: session } = await supabase.auth.getSession();
  const userId = session?.session?.user?.id;

  const { data: nodes } = await (supabase as any)
    .from("thematic_nodes")
    .select("id, publish_priority")
    .in("id", nodeIds);

  if (!nodes) return 0;

  const queueItems = nodes.map((n: any) => ({
    user_id: userId,
    map_id: mapId,
    node_id: n.id,
    step: "full_article",
    status: "queued",
    priority: n.publish_priority || 50,
    config: {},
  }));

  await (supabase as any).from("generation_queue").insert(queueItems);
  return queueItems.length;
}

// =============================================
// Processing loop (atomic claim via RPC)
// =============================================

export interface ProcessingProgress {
  current: number;
  total: number;
  nodeTitle?: string;
  stage?: string;
  paused: boolean;
}

interface ClaimedJob {
  id: string;
  user_id: string;
  map_id: string;
  node_id: string;
  step: string;
  priority: number;
  config: Record<string, unknown>;
}

/**
 * Atomically claim a single queued job via FOR UPDATE SKIP LOCKED RPC.
 * Returns null if no job available (queue empty OR all locked by other sessions).
 */
async function claimNextJob(sessionId: string): Promise<ClaimedJob | null> {
  const { data, error } = await (supabase as any).rpc("claim_generation_job", {
    p_session_id: sessionId,
    p_lock_minutes: LOCK_LEASE_MINUTES,
  });
  if (error) {
    console.error("[batch] claim_generation_job RPC error:", error);
    return null;
  }
  if (!data || data.length === 0) return null;
  return data[0] as ClaimedJob;
}

/**
 * Heartbeat: extend lock_expires_at for jobs currently locked by this session.
 * Prevents stale recovery from grabbing jobs we're still actively processing.
 */
async function heartbeatLocks(sessionId: string, activeJobIds: Set<string>): Promise<void> {
  if (activeJobIds.size === 0) return;
  const newExpiry = new Date(Date.now() + LOCK_LEASE_MINUTES * 60 * 1000).toISOString();
  await (supabase as any)
    .from("generation_queue")
    .update({ lock_expires_at: newExpiry })
    .eq("locked_by", sessionId)
    .in("id", Array.from(activeJobIds));
}

/**
 * Cleanup lock fields after job reaches terminal state.
 */
async function clearLock(jobId: string, status: "completed" | "failed", patch: Record<string, unknown>): Promise<void> {
  await (supabase as any)
    .from("generation_queue")
    .update({
      ...patch,
      status,
      locked_by: null,
      locked_at: null,
      lock_expires_at: null,
      completed_at: new Date().toISOString(),
    })
    .eq("id", jobId);
}

/**
 * Process the generation queue. Runs until empty or limits reached.
 * Atomic claim via FOR UPDATE SKIP LOCKED → 2 sessions never grab the same job.
 * Stale lock recovery: jobs with lock_expires_at < NOW() get re-claimed by the next worker.
 */
export async function processQueue(
  config: Partial<BatchConfig> = {},
  onProgress?: (progress: ProcessingProgress) => void,
): Promise<{ processed: number; failed: number; totalCost: number }> {
  const cfg = { ...DEFAULT_CONFIG, ...config };

  // Worker Mode: delegate to server-side worker via HTTP (no browser loop)
  // Feature flag: worker_config.use_worker_mode.value = true
  if (await isWorkerModeEnabled()) {
    const { data: queued } = await (supabase as any)
      .from("generation_queue")
      .select("node_id, map_id")
      .eq("status", "queued");
    const byMap = new Map<string, string[]>();
    for (const row of (queued || []) as Array<{ node_id: string; map_id: string }>) {
      const arr = byMap.get(row.map_id) || [];
      arr.push(row.node_id);
      byMap.set(row.map_id, arr);
    }
    let enqueued = 0;
    for (const [mapId, nodeIds] of byMap.entries()) {
      try {
        const res = await enqueueGenerationJob(mapId, nodeIds);
        enqueued += res.enqueued;
      } catch (err) {
        console.error("[batch] worker enqueue failed:", err);
      }
    }
    onProgress?.({ current: 0, total: enqueued, paused: false, stage: "enqueued_to_worker" });
    return { processed: enqueued, failed: 0, totalCost: 0 };
  }

  if (isProcessing) {
    throw new Error("Queue jest już przetwarzane.");
  }

  // sessionId — unique per processQueue() invocation. Used for lock ownership.
  const sessionId =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  isProcessing = true;
  isPaused = false;

  let processed = 0;
  let failed = 0;
  let totalCost = 0;

  // Active jobs tracked for heartbeat
  const activeJobIds = new Set<string>();
  const heartbeatTimer = setInterval(() => {
    heartbeatLocks(sessionId, activeJobIds).catch((e) =>
      console.error("[batch] heartbeat failed:", e),
    );
  }, HEARTBEAT_INTERVAL_MS);

  try {
    while (true) {
      if (isPaused) {
        onProgress?.({ current: processed, total: 0, paused: true });
        await new Promise((resolve) => setTimeout(resolve, 2000));
        continue;
      }

      // Check daily limits
      if (cfg.budgetMode) {
        const status = await getQueueStatus();
        if (status.todayCount >= cfg.maxDailyGenerations) {
          console.log("Daily generation limit reached:", status.todayCount);
          break;
        }
        if (status.todayCost >= cfg.maxDailyCost) {
          console.log("Daily cost limit reached:", status.todayCost);
          break;
        }
      }

      // Atomically claim N jobs (one RPC call per concurrency slot).
      // Each call acquires its own row-level lock — no two sessions grab the same job.
      const claimed: ClaimedJob[] = [];
      for (let i = 0; i < cfg.maxConcurrentGenerations; i++) {
        const job = await claimNextJob(sessionId);
        if (!job) break;
        claimed.push(job);
      }

      if (claimed.length === 0) break;

      // Track for heartbeat
      claimed.forEach((j) => activeJobIds.add(j.id));

      // Total queued for progress (post-claim count)
      const { count: totalQueued } = await (supabase as any)
        .from("generation_queue")
        .select("id", { count: "exact", head: true })
        .eq("status", "queued");

      // Process claimed batch concurrently
      const promises = claimed.map(async (item) => {
        try {
          // Get node title for progress
          const { data: node } = await (supabase as any)
            .from("thematic_nodes")
            .select("title")
            .eq("id", item.node_id)
            .single();

          onProgress?.({
            current: processed + 1,
            total: (totalQueued || 0) + processed,
            nodeTitle: node?.title,
            stage: "generating",
            paused: false,
          });

          const result = await generateArticle(item.node_id, item.map_id);

          await clearLock(item.id, result.success ? "completed" : "failed", {
            result: { cost: result.cost, durationMs: result.durationMs },
            error: result.error || null,
          });

          if (result.success) {
            processed++;
            totalCost += result.cost.total;

            // Auto-publish if enabled — graceful skip gdy worker OFF (2026-04-22)
            if (cfg.autoPublish) {
              const { isWorkerModeEnabled } = await import("@/lib/workerMode");
              if (await isWorkerModeEnabled()) {
                await (supabase as any).from("publish_queue").insert({
                  user_id: item.user_id,
                  map_id: item.map_id,
                  node_id: item.node_id,
                  status: "queued",
                });
              } else {
                console.warn(
                  `[batchOrchestrator] Auto-publish SKIPPED for node=${item.node_id.slice(0, 8)} — worker mode OFF`,
                );
              }
            }
          } else {
            failed++;
          }
        } catch (err) {
          await clearLock(item.id, "failed", {
            error: err instanceof Error ? err.message : "Unknown error",
          });
          failed++;
        } finally {
          activeJobIds.delete(item.id);
        }
      });

      await Promise.all(promises);
    }
  } finally {
    clearInterval(heartbeatTimer);
    isProcessing = false;
  }

  return { processed, failed, totalCost };
}

/**
 * Pause queue processing.
 */
export function pauseQueue(): void {
  isPaused = true;
}

/**
 * Resume queue processing.
 */
export function resumeQueue(): void {
  isPaused = false;
}

/**
 * Check if queue is currently processing.
 */
export function isQueueProcessing(): boolean {
  return isProcessing;
}

/**
 * Retry failed items (reset to queued + clear lock fields).
 */
export async function retryFailed(mapId?: string): Promise<number> {
  let query = (supabase as any)
    .from("generation_queue")
    .update({
      status: "queued",
      error: null,
      started_at: null,
      completed_at: null,
      locked_by: null,
      locked_at: null,
      lock_expires_at: null,
    })
    .eq("status", "failed");

  if (mapId) query = query.eq("map_id", mapId);

  const { data } = await query.select("id");
  return data?.length || 0;
}
